import React, { useState, useCallback, useEffect } from 'react';
import { Mail, ExternalLink, Paperclip } from 'lucide-react';
import { openDrawer } from 'Services/Drawer';
import T from 'Components/Helpers/translate';
import AssistantResource from 'Resources/Assistant';
import AgentApprovalsResource from 'Resources/AgentApprovals';
import ProposalCardShell, { type StatusPill } from 'App/shadcn/pages/AIWorkspace/components/ProposalCardShell';
import { executionFailureMessage } from './approvalAdapters';
import type { EmailDraft } from './types';

const htmlToPlainText = (html: string): string => {
	if (typeof window === 'undefined') return html;
	const div = document.createElement('div');
	div.innerHTML = html
		.replace(/<\/?(p|div)[^>]*>/gi, '\n')
		.replace(/<br\s*\/?>/gi, '\n')
		.replace(/<\/li>/gi, '\n');
	const text = div.textContent ?? '';
	return text
		.replace(/\r\n/g, '\n')
		.replace(/\n{3,}/g, '\n\n')
		.trim();
};

const extractMailIds = (raw: unknown): number[] | undefined => {
	if (!raw || typeof raw !== 'object') return undefined;
	const ids = (raw as { mailIds?: unknown }).mailIds;
	if (!Array.isArray(ids)) return undefined;
	const out = ids.map(v => Number(v)).filter(n => Number.isFinite(n));
	return out.length ? out : undefined;
};

type Props = {
	draft: EmailDraft;
	onUpdate?: (update: Partial<EmailDraft>) => void;
	/** When true, the card renders in a fully inert state — no send /
	 *  cancel buttons, no inputs are editable. Used by the trace drawer
	 *  when displaying an already-resolved approval. */
	readOnly?: boolean;
};

type ResolvedContact = { id: number; name: string; email: string };

const resolveContacts = async (ids: number[]): Promise<ResolvedContact[]> => {
	if (!ids.length) return [];
	const results: ResolvedContact[] = [];
	await Promise.all(
		ids.map(async id => {
			try {
				const res = await fetch(`/api/v2/contacts/${id}`);
				if (!res.ok) return;
				const body = await res.json();
				const d = body.data;
				const name = d?.name || [d?.firstName, d?.lastName].filter(Boolean).join(' ') || `#${id}`;
				results.push({ id, name, email: d?.email || '' });
			} catch {
				results.push({ id, name: `#${id}`, email: '' });
			}
		})
	);
	return results;
};

function statusToPill(status: EmailDraft['status']): StatusPill | null {
	switch (status) {
		case 'sending':
			return { label: T('assistant.emailSending') || 'Sending…', tone: 'saving' };
		case 'sent':
			return { label: T('assistant.emailSent') || 'Sent', tone: 'ok' };
		case 'cancelled':
			return { label: T('assistant.cancelled') || 'Cancelled', tone: 'muted' };
		case 'error':
			return { label: T('default.failed') || 'Failed', tone: 'error' };
		case 'pending':
		default:
			return null;
	}
}

const EmailDraftCard = ({ draft, onUpdate, readOnly = false }: Props) => {
	const [editedSubject, setEditedSubject] = useState(draft.subject);
	const [editedBody, setEditedBody] = useState(draft.body);
	const [toContacts, setToContacts] = useState<ResolvedContact[]>([]);
	const [ccContacts, setCcContacts] = useState<ResolvedContact[]>([]);
	const [signatureText, setSignatureText] = useState<string | null>(null);
	const [signatureMerged, setSignatureMerged] = useState(false);

	const isSending = draft.status === 'sending';
	const resolved = draft.status === 'sent' || draft.status === 'cancelled';
	const errored = draft.status === 'error';

	useEffect(() => {
		let active = true;
		void resolveContacts(draft.contactIds).then(contacts => {
			if (active) setToContacts(contacts);
		});
		if (draft.ccContactIds?.length) {
			void resolveContacts(draft.ccContactIds).then(contacts => {
				if (active) setCcContacts(contacts);
			});
		}
		return () => {
			active = false;
		};
	}, [draft.contactIds.join(','), draft.ccContactIds?.join(',')]);

	useEffect(() => {
		let active = true;
		void fetch('/api/v2/mailSignature/active-for-user')
			.then(r => (r.ok ? r.json() : null))
			.then(body => {
				if (!active) return;
				const html = body?.data?.body;
				if (typeof html !== 'string' || !html.trim()) {
					setSignatureText(null);
					return;
				}
				setSignatureText(htmlToPlainText(html));
			})
			.catch(() => {
				if (active) setSignatureText(null);
			});
		return () => {
			active = false;
		};
	}, []);

	// Once the signature text has loaded, splice it into the message body so
	// the textarea reads as one continuous email (body + signature) — which
	// is what the recipient will see. We do this exactly once per card; if
	// the rep then edits, their edits stick. The `signatureMerged` flag
	// signals the server to skip its own append step at send time.
	useEffect(() => {
		if (!signatureText || signatureMerged) return;
		setEditedBody(prev => (prev.includes(signatureText) ? prev : `${prev.trimEnd()}\n\n${signatureText}`));
		setSignatureMerged(true);
	}, [signatureText, signatureMerged]);

	const handleSend = useCallback(async () => {
		if (!onUpdate) return;
		onUpdate({ status: 'sending' });
		try {
			let mailIds: number[] | undefined;
			let failure: string | null = null;
			if (draft.approvalId) {
				const modified = {
					contactIds: draft.contactIds,
					subject: editedSubject,
					body: editedBody,
					ccContactIds: draft.ccContactIds,
					clientId: draft.clientId,
					// Attachments are immutable; pass the original ids through so the
					// executor (which runs the modified payload) keeps them.
					...(draft.attachments?.length && { attachmentFileIds: draft.attachments.map(a => a.id) }),
					// Signature was merged into the body in the preview, so the
					// server should not re-append the configured signature.
					...(signatureMerged && { signatureIncluded: true })
				};
				const { approval } = await AgentApprovalsResource.approve(draft.approvalId, undefined, modified);
				mailIds = extractMailIds(approval.resultPayload);
				failure = executionFailureMessage(approval.resultPayload as Record<string, unknown> | null | undefined);
			} else {
				const result = await AssistantResource.executeSendEmail(
					draft.contactIds,
					editedSubject,
					editedBody,
					draft.ccContactIds,
					draft.clientId
				);
				mailIds = extractMailIds(result);
				failure = executionFailureMessage(result as Record<string, unknown> | null | undefined);
			}
			if (failure !== null) {
				onUpdate({
					status: 'error',
					subject: editedSubject,
					body: editedBody,
					error: failure || T('default.failed') || 'Send failed'
				});
			} else {
				onUpdate({ status: 'sent', subject: editedSubject, body: editedBody, mailIds });
			}
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Send failed';
			onUpdate({ status: 'error', error: message });
		}
	}, [
		draft.approvalId,
		draft.contactIds,
		draft.ccContactIds,
		draft.clientId,
		draft.attachments,
		editedSubject,
		editedBody,
		signatureMerged,
		onUpdate
	]);

	const handleCancel = useCallback(async () => {
		if (!onUpdate) return;
		if (draft.approvalId) {
			try {
				// Persist the user's edits on reject too, so a reload of the
				// rejected card surfaces what the rep actually saw — not the
				// agent's original draft text.
				const editsDiffer = editedSubject !== draft.subject || editedBody !== draft.body;
				const modified = editsDiffer
					? {
							contactIds: draft.contactIds,
							subject: editedSubject,
							body: editedBody,
							ccContactIds: draft.ccContactIds,
							clientId: draft.clientId
					  }
					: undefined;
				await AgentApprovalsResource.reject(draft.approvalId, 'cancelled_in_chat', modified);
			} catch {
				/* non-fatal — local state still flips to cancelled */
			}
		}
		onUpdate({ status: 'cancelled' });
	}, [
		draft.approvalId,
		draft.subject,
		draft.body,
		draft.contactIds,
		draft.ccContactIds,
		draft.clientId,
		editedSubject,
		editedBody,
		onUpdate
	]);

	const handleRetry = useCallback(() => {
		onUpdate?.({ status: 'pending', error: undefined });
	}, [onUpdate]);

	const recipientLabelShort = toContacts.length
		? toContacts.map(c => c.name).join(', ')
		: draft.contactIds.map(id => `#${id}`).join(', ');
	const recipientLabelLong = toContacts.length
		? toContacts.map(c => (c.email ? `${c.name} <${c.email}>` : c.name)).join(', ')
		: draft.contactIds.map(id => `#${id}`).join(', ');
	const ccLabel = ccContacts.length ? ccContacts.map(c => c.name).join(', ') : null;

	const titleParts = [T('mail.mail') || 'Email'];
	if (recipientLabelShort) titleParts.push(recipientLabelShort);
	if (editedSubject.trim()) titleParts.push(editedSubject.trim());
	const title = titleParts.join(' · ');

	const inputsDisabled = isSending || readOnly || resolved;

	const body = (
		<div className="pr-email-body">
			{errored && draft.error && <div className="pr-email-error">{draft.error}</div>}
			<dl className="pr-email-fields">
				<dt>{T('mail.to') || 'To'}</dt>
				<dd>{recipientLabelLong}</dd>
				{ccLabel && (
					<>
						<dt>{T('mail.cc') || 'CC'}</dt>
						<dd>{ccLabel}</dd>
					</>
				)}
				<dt>{T('mail.subject') || 'Subject'}</dt>
				<dd>
					<input
						type="text"
						className="pr-email-input"
						value={editedSubject}
						onChange={e => setEditedSubject(e.target.value)}
						disabled={inputsDisabled}
					/>
				</dd>
				<dt className="pr-email-label-multiline">{T('mail.body') || 'Message'}</dt>
				<dd>
					<textarea
						className="pr-email-input pr-email-textarea"
						value={editedBody}
						onChange={e => setEditedBody(e.target.value)}
						disabled={inputsDisabled}
						rows={Math.min(16, Math.max(6, editedBody.split('\n').length + 1))}
					/>
				</dd>
				{!!draft.attachments?.length && (
					<>
						<dt>{T('mail.attachments') || 'Attachments'}</dt>
						<dd>
							<ul className="pr-email-attachments">
								{draft.attachments.map(a => (
									<li key={a.id}>
										<Paperclip style={{ width: 12, height: 12 }} />
										<span>{a.name}</span>
									</li>
								))}
							</ul>
						</dd>
					</>
				)}
			</dl>
		</div>
	);

	const canApprove = !readOnly && !resolved && (errored || (!isSending && editedSubject.trim() && editedBody.trim()));

	const firstMailId = draft.mailIds?.[0];
	const handleOpenMail = useCallback(() => {
		if (firstMailId != null) openDrawer('SentMail', { mail: { id: firstMailId } });
	}, [firstMailId]);
	const headerAction =
		draft.status === 'sent' && firstMailId != null ? (
			<button
				type="button"
				className="ws-btn ws-btn-secondary ws-prop-mini-btn"
				onClick={handleOpenMail}
				title={T('default.entity.view', { entity: T('mail.mail') })}
			>
				<ExternalLink style={{ width: 12, height: 12 }} />
				<span>{T('default.open')}</span>
			</button>
		) : null;

	return (
		<div className="pr-email-card-wrapper">
			<ProposalCardShell
				icon={Mail}
				title={title}
				chips={draft.createdDate ? [draft.createdDate] : undefined}
				statusPill={statusToPill(draft.status)}
				headerAction={headerAction}
				body={body}
				busy={isSending}
				resolved={resolved}
				approveLabel={errored ? T('assistant.retry') : T('assistant.emailSend') || 'Send'}
				rejectLabel={T('cancel') || T('assistant.cancel')}
				onApprove={canApprove ? (errored ? handleRetry : handleSend) : undefined}
				onReject={readOnly || resolved || errored ? undefined : handleCancel}
			/>
		</div>
	);
};

export default EmailDraftCard;
